/* eslint-disable react/prop-types */

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';

import AuthenticatedContainer from 'components/AuthenticatedContainer';
import GridItem from 'components/Grid/GridItem.js';
import { getConfig } from '../../config';
import EVChargingService from '../../services/EVChargingService';
import useAPI from 'useAPI';
import { Context } from 'Store';

const config = getConfig();
const stripePromise = loadStripe(config.stripePublishKey);

function EnrollmentSetupForm({ clientSecret, user, onCancel, onComplete }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isComplete, setIsComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!stripe || !elements) return;

    setSubmitting(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setSubmitting(false);
      return;
    }

    // Confirm the SetupIntent in-page (no redirect) so we can finalize
    // enrollment server-side immediately afterward.
    const result = await stripe.confirmSetup({
      elements,
      clientSecret,
      redirect: 'if_required',
    });

    if (result.error) {
      setError(result.error.message);
      setSubmitting(false);
      return;
    }

    const setupIntent = result.setupIntent;
    if (!setupIntent || setupIntent.status !== 'succeeded' || !setupIntent.payment_method) {
      setError('Payment method setup did not complete. Please try again.');
      setSubmitting(false);
      return;
    }

    try {
      const status = await EVChargingService.completeEnrollment(setupIntent.payment_method);
      onComplete(status);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Saved your card, but we could not finalize enrollment. Please try again.',
      );
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
        Save a credit card for EV Charging. We charge a small top-up to your
        card and credit your charging wallet; each session is deducted from
        that balance at the rates posted at each location. When your balance
        runs low, we top up again automatically. You can stop charging your
        card any time by cancelling enrollment.
      </Typography>

      <PaymentElement
        options={{
          paymentMethodOrder: ['card'],
          defaultValues: {
            billingDetails: {
              name: user?.name || '',
            },
          },
          fields: {
            billingDetails: {
              name: 'auto',
            },
          },
        }}
        onChange={(e) => setIsComplete(e.complete)}
      />

      {error && (
        <Alert severity='error' sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onCancel} disabled={submitting} color='secondary'>
          Cancel
        </Button>
        <Button
          type='submit'
          variant='contained'
          color='primary'
          disabled={!stripe || !isComplete || submitting}
        >
          {submitting ? <CircularProgress size={18} /> : 'Save Payment Method'}
        </Button>
      </Box>
    </form>
  );
}

export default function EVChargingPayment() {
  const navigate = useNavigate();
  const api = useAPI();
  const { state } = useContext(Context);
  const { user } = state;
  const [clientSecret, setClientSecret] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const loadSetupIntent = useCallback(async () => {
    try {
      const data = await EVChargingService.createSetupIntent();
      setClientSecret(data.clientSecret);
    } catch (err) {
      setLoadError(
        err?.response?.data?.message ||
          'Unable to initialize payment setup. Please try again later.',
      );
    }
  }, []);

  useEffect(() => {
    if (api) {
      EVChargingService.init(api);
      loadSetupIntent();
    }
  }, [api, loadSetupIntent]);

  return (
    <AuthenticatedContainer>
      <GridItem xs={12} sm={12} md={8}>
        <Box sx={{ px: 2, py: 3 }}>
          <Typography variant='h5' sx={{ color: '#324152', mb: 2 }}>
            Payment Method
          </Typography>

          {!clientSecret && !loadError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {loadError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {loadError}
            </Alert>
          )}

          {clientSecret && (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret, appearance: { theme: 'stripe' } }}
            >
              <EnrollmentSetupForm
                clientSecret={clientSecret}
                user={user}
                onCancel={() => navigate('/ev-charging')}
                onComplete={() => navigate('/ev-charging')}
              />
            </Elements>
          )}
        </Box>
      </GridItem>
    </AuthenticatedContainer>
  );
}
